import 'xterm/css/xterm.css';
import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import classes from 'classnames';
import useMonacoEditor from "../../../components/useMonacoEditor";
import styles from './index.module.less';
import {Button, ButtonGroup, Classes, Dialog, Icon, Intent, Tag, Toaster} from "@blueprintjs/core";
import {useDebounce, useSize} from "@umijs/hooks";
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import {Position} from "monaco-editor";
import {useParams} from "react-router-dom";
import emitter from "../../../utils/emitter";
import axios from 'axios';

const toaster = Toaster.create({
  position: Position.TOP,
  usePortal: true
});

const TerminalColor = {
  WHITE: `\x1b[1;37m`,
  RED: `\x1b[1;31m`,
  GREEN: `\x1b[1;32m`,
};

const CodingEditor = memo(({ className, style, lesson, setLesson }) => {
  const { editorRef, editorReLayout, resetEditor, getEditorCode, setEditorCode } = useMonacoEditor();
  const terminalContainerRef = useRef();
  const terminal = useRef(new Terminal({
    disableStdin: false,
    convertEol: true
  }));
  const terminalFitAddon = useRef(new FitAddon());
  const [containerSize, containerRef] = useSize();
  const [headerSize, headerRef] = useSize();
  const [footerSize, footerRef] = useSize();
  const [editorHeight, setEditorHeight] = useState(0);
  const containerSizeDebounced = useDebounce(containerSize, 300);
  const headerSizeDebounced = useDebounce(headerSize, 300);
  const footerSizeDebounced = useDebounce(footerSize, 300);
  const [exerciseTagShow, setExerciseTagShow] = useState(false);
  const { lessonId } = useParams();
  const [codeRunning, setCodeRunning] = useState(false);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [codeErrorCount, setCodeErrorCount] = useState(0);
  const exerciseInfo = useRef({
    id: null,
    status: null,
    initCode: null,
    defaultCode: null,
    fixed: false
  });
  const [fullscreen, setFullscreen] = useState(false);

  const clearExerciseInfo = () => {
    exerciseInfo.current = {
      id: null,
      status: null,
      initCode: null,
      defaultCode: null,
      fixed: false,
    }
  };

  // 运行文章中的代码块
  useEffect(() => {
    emitter.on('run-block-code', handleInitBlockCodeFromArticle);
    return () => {
      emitter.off('run-block-code', handleInitBlockCodeFromArticle);
    }
  }, [lesson]);

  // 运行练习题代码
  useEffect(() => {
    emitter.on('copy-question-code', handleInitCodeEditorFromExercise);
    return () => {
      emitter.off('copy-question-code', handleInitCodeEditorFromExercise);
    }
  }, [lesson]);

  useEffect(() => {
    if (codeErrorCount >= 3) {
      axios.get('/api/course/instruction_answer', {
        params: {
          instruction_id: exerciseInfo.current.id
        }
      }).then((response => {
        const { code, data } = response.data;
        if (code !== 0) {
          return;
        }
        setCodeErrorCount(0);
        lesson.exercises.forEach(exercise => {
          if (exercise.id === exerciseInfo.current.id) {
            exercise.showErrorCode = true;
            exercise.expectCode = data.expect_code;
          }
        });
        if (exerciseInfo.current.fixed) {
          lesson.expectCode = data.expect_code;
        }
        setLesson({ ...lesson });
      }));

    }
  }, [codeErrorCount, lesson, setLesson]);

  // 组件被卸载或者章节改变的时候，代码被保存
  useEffect(() => {
    terminal.current.clear();
    clearExerciseInfo();
    setCodeErrorCount(0);
    resetEditor();
  }, [lessonId]);

  // 计算编辑器的高度
  useEffect(() => {
    if (typeof containerSizeDebounced.height === 'undefined') {
      return;
    }
    if (typeof headerSizeDebounced.height === 'undefined') {
      return;
    }
    if (typeof footerSizeDebounced.height === 'undefined') {
      return;
    }
    setEditorHeight(containerSizeDebounced.height - headerSizeDebounced.height - footerSizeDebounced.height);
  }, [containerSizeDebounced, headerSizeDebounced, footerSizeDebounced]);

  // 重新布局代码编辑器
  useEffect(editorReLayout, [editorHeight]);

  // 初始化终端
  useEffect(() => {
    terminal.current.loadAddon(terminalFitAddon.current);
    terminal.current.open(terminalContainerRef.current);
    terminalFitAddon.current.fit();
  }, []);

  // 重新布局终端
  useEffect(() => {
    terminalFitAddon.current.fit();
  }, [footerSizeDebounced]);

  const terminalWriteLine = (line, color = TerminalColor.WHITE, clean = false) => {
    if (clean === true) {
      terminal.current.clear();
    }
    terminal.current.writeln(`${color}${line}`);
  };

  const handleInitBlockCodeFromArticle = (code) => {
    setExerciseTagShow(false);
    terminal.current.clear();
    setEditorCode(code);
    document.querySelector('.code-runner').click();
  };

  const handleInitCodeEditorFromExercise = useCallback(({ code, id, title, status, defaultCode, fixed = false }) => {
    exerciseInfo.current.id = id;
    exerciseInfo.current.status = status;
    exerciseInfo.current.initCode = code;
    exerciseInfo.current.defaultCode = defaultCode;
    exerciseInfo.current.fixed = fixed;
    terminalWriteLine(`【正在完成练习题：${title.replace(/\n/g, '')}】`, TerminalColor.GREEN, true);
    setExerciseTagShow(!fixed);
    setCodeErrorCount(0);
    setEditorCode(code);
    terminal.current.clear();
    toaster.show({
      message: '代码初始化成功，点击下方"运行"按钮即可运行代码',
      intent: Intent.SUCCESS,
      timeout: 1000
    });
    if (fixed) {
      return;
    }
    lesson.exercises.forEach(exercise => {
      if (exercise.id === id) {
        exercise.status = 'doing'
      }
    });
    setLesson({ ...lesson });
  }, [setLesson, setEditorCode, lesson]);

  const handlePollCodeRunningResult = (submitToken) => {
    axios.get('/api/judge/submit_result', {
      params: {
        submit_token: submitToken
      }
    }).then(response => {
      const { code, data } = response.data;
      if (code === 0) {
        if (data.status.id < 3) {
          setTimeout(() => handlePollCodeRunningResult(submitToken), 1000);
          return;
        }
        if (data.status.id > 3) {
          terminalWriteLine(data.stderr, TerminalColor.RED);
        } else {
          terminalWriteLine(`代码执行成功，用时：${data.time}秒，内存消耗：${(data.memory / 1024).toFixed(2)}MB`, TerminalColor.GREEN);
          terminalWriteLine(data.stdout, TerminalColor.WHITE);
        }
        setCodeRunning(false);
      }
    });
  };

  const executeUserCode = (code) => {
    terminalWriteLine('代码正在执行请稍后...', TerminalColor.GREEN, true);
    axios.post('/api/judge/submit', {
      source_code: code,
      language_id: 71,
      srcfrom: 'course_run',
      out_object_id: lessonId,
      code_run_type: lesson.codeRunType
    }).then(response => {
      const { code, data } = response.data;
      if (code !== 0) {
        toaster.show({
          message: '服务器错误，请稍后重试',
          intent: Intent.DANGER,
          timeout: 1000
        });
        setCodeRunning(false);
        return;
      }
      handlePollCodeRunningResult(data.submit_token);
    });
  };

  const executeExerciseCode = (code) => {
    axios.post('/api/course/instruction_progress_submit', {
      instruction_id: exerciseInfo.current.id,
      answer: JSON.stringify({code})
    }).then((response) => {
      setCodeRunning(false);
      const { code, data } = response.data;
      if (code !== 0) {
        toaster.show({
          message: '代码执行错误',
          intent: Intent.DANGER,
          timeout: 1000
        });
        return;
      }
      const { status, message, stdout, stderr, judge_cases: judgeCases } = data;
      if (status === 'right') {
        toaster.show({
          message,
          intent: Intent.SUCCESS,
          timeout: 1000
        });
        lesson.exercises = lesson.exercises.map(exercise => {
          if (String(exercise.id) === String(exerciseInfo.current.id)) {
            return {
              ...exercise,
              status,
            }
          }
          return exercise;
        });
        if (judgeCases && exerciseInfo.current.fixed) {
          terminalWriteLine('挑战成功，详情请查看左侧测试用例', TerminalColor.GREEN);
          lesson.judgeCases = JSON.parse(judgeCases);
        } else {
          terminalWriteLine(stdout, TerminalColor.WHITE);
          setExerciseTagShow(false);
          clearExerciseInfo();
        }
      } else if (status === 'wrong') {
        toaster.show({
          message,
          intent: Intent.DANGER,
          timeout: 1000
        });
        setCodeErrorCount(codeErrorCount + 1);
        if (judgeCases && exerciseInfo.current.fixed) {
          terminalWriteLine('挑战失败，详情请查看左侧测试用例', TerminalColor.RED);
          terminalWriteLine(stderr);
          lesson.judgeCases = JSON.parse(judgeCases);
        } else {
          terminalWriteLine(stderr, TerminalColor.RED);
        }
      }
      setLesson({ ...lesson });
    });
  };

  const handleRunCode = () => {
    const code = getEditorCode();
    terminal.current.clear();
    setCodeRunning(true);
    if (exerciseInfo.current.id === null) {
      executeUserCode(code);
    } else {
      executeExerciseCode(code);
    }
  };

  const handleResetCode = () => {
    if (exerciseInfo.current.id) {
      setEditorCode(exerciseInfo.current.defaultCode);
      return;
    }
    resetEditor();
  };

  const handleCancelExercise = () => {
    lesson.exercises.forEach(exercise => {
      if (exercise.id === exerciseInfo.current.id) {
        exercise.status = exerciseInfo.current.status;
      }
    });
    setLesson({  ...lesson });
    clearExerciseInfo();
    if (terminal.current) {
      terminal.current.clear();
    }
    setExerciseTagShow(false)
  };

  return (
    <div className={classes(styles['code-runner-editor'], className)} style={style} ref={containerRef}>
      <div className={classes('code-editor', styles.body, lesson.loading && Classes.SKELETON)} ref={editorRef} style={{ height: editorHeight }} />
      <div className={styles.footer} ref={footerRef}>
        <div className={classes('terminal', styles.terminal, lesson.loading && Classes.SKELETON)} ref={terminalContainerRef} />
      </div>
      <div className={styles.actions} ref={headerRef}>
        <ButtonGroup className={classes(lesson.loading && Classes.SKELETON)}>
          <Button className='code-runner' icon='play' loading={codeRunning} onClick={handleRunCode}>运行代码</Button>
          <Button className='code-debugger' icon='eye-open' onClick={() => setDebugDialogOpen(true)}>可视化运行</Button>
          <Button className='code-reset' icon='reset' onClick={handleResetCode}>重置代码</Button>
        </ButtonGroup>
        <div className={styles.indicator}>
          {exerciseTagShow && (
            <Tag className='exercise-tips' large icon={<Icon icon='dot' className={styles.dot} />} intent={Intent.WARNING} onRemove={handleCancelExercise}>正在完成编程题，点击关闭回到练习模式</Tag>
          )}
        </div>
      </div>
      <Dialog onClose={() => setDebugDialogOpen(false)} canEscapeKeyClose canOutsideClickClose className={classes(styles['debug-dialog'], Classes.DARK, fullscreen && styles.fullscreen)} isOpen={debugDialogOpen}>
        <div className={classes(Classes.DIALOG_HEADER)}>
          <span className="bp3-icon-large bp3-icon-eye-open" />
          <h4 className="bp3-heading">代码可视化</h4>
          <button onClick={() => setFullscreen(!fullscreen)} aria-label="Close" className={classes('bp3-dialog-close-button bp3-button bp3-minimal', fullscreen ? 'bp3-icon-minimize' : 'bp3-icon-fullscreen')} />
          <button onClick={() => setDebugDialogOpen(false)} aria-label="Close" className="bp3-dialog-close-button bp3-button bp3-minimal bp3-icon-cross" />
        </div>
        <div className={classes(Classes.DIALOG_BODY, styles.body)}>
          {debugDialogOpen && (
            <iframe
              title='debug-code'
              width="100%"
              height='100%'
              frameBorder="0"
              src={`http://pytutor.pythontip.com/iframe-embed.html#code=${encodeURIComponent(getEditorCode())}&codeDivHeight=800&codeDivWidth=600&cumulative=false&curInstr=0&heapPrimitives=nevernest&origin=opt-frontend.js&py=3&rawInputLstJSON=%5B%5D&textReferences=false`}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
});

export default CodingEditor;
